/**
 * Numbers of a statistics page, written the way the reader's language writes them — the same
 * conventions as the methods paragraph and the exported file, which the server words.
 */

const formatters = new Map<string, Intl.NumberFormat>();

// The narrow no-break space typography puts before « % ». Built from its code point: the character
// itself is invisible in an editor and the linter rightly refuses it in source.
const PERCENT = `${String.fromCharCode(0x202f)}%`;

function formatter(locale: string, digits: number): Intl.NumberFormat {
  const key = `${locale}:${digits}`;
  let found = formatters.get(key);
  if (!found) {
    found = new Intl.NumberFormat(locale, {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    });
    formatters.set(key, found);
  }
  return found;
}

export function formatNumber(
  value: number | null | undefined,
  locale: string,
  digits = 2,
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return formatter(locale, digits).format(value);
}

/** A measure as a person writes it: whole when whole, one decimal past ten, two below. */
export function formatMeasure(value: number | null | undefined, locale: string): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  if (Math.abs(value - Math.round(value)) < 1e-9) return formatNumber(value, locale, 0);
  return formatNumber(value, locale, Math.abs(value) >= 10 ? 1 : 2);
}

/** A p-value as journals print it: three decimals, « < 0,001 » below. */
export function formatP(p: number | null | undefined, locale: string): string {
  if (p === null || p === undefined || Number.isNaN(p)) return "—";
  if (p < 0.001) return `< ${formatNumber(0.001, locale, 3)}`;
  return formatNumber(p, locale, 3);
}

export function formatPercent(part: number, whole: number, locale: string): string {
  if (whole <= 0) return "—";
  return `${formatNumber((100 * part) / whole, locale, 1)}${PERCENT}`;
}

export function formatShare(value: number | null | undefined, locale: string): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return `${formatNumber(100 * value, locale, 1)}${PERCENT}`;
}

export function formatInterval(
  low: number | null | undefined,
  high: number | null | undefined,
  locale: string,
  digits = 2,
): string {
  if (low === null || low === undefined || high === null || high === undefined) return "";
  return `[${formatNumber(low, locale, digits)} ; ${formatNumber(high, locale, digits)}]`;
}
